import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, model, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DocumentDraftPanelComponent, InvoiceTableComponent } from '@foundation/invoices/ui';
import { NotificationService } from '@foundation/notification';
import { ReceiptTableComponent } from '@foundation/receipts/ui';
import { TranslateDirective, TranslationService } from '@foundation/translations/services';
import { UserModals } from '@foundation/users/modals';
import { RequestResponse } from '@foundation/network/services';
import { Observable } from 'rxjs';

import {
	Invoice,
	InvoiceConfig,
} from '@foundation/invoices/models';
import { v4 as uuidv4 } from 'uuid';
import { InvoicesRepository } from '@foundation/invoices/state';
import { Receipt, ReceiptConfig } from '@foundation/receipts/models';
import { ReceiptsRepository } from '@foundation/receipts/state';

import { DocumentKind } from './admin-accounting.models';

const DEFAULT_TAB: DocumentKind = 'invoice';

@Component({
	selector: 'lib-admin-accounting-page',
	imports: [CommonModule, FormsModule, TranslateDirective, InvoiceTableComponent, ReceiptTableComponent, DocumentDraftPanelComponent],
	templateUrl: './admin-accounting-page.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminAccountingPageComponent {
	/** Drafting a document belongs to that document's own repository: what the admin endpoint
	 * expects follows from its config class, not from this screen. */
	private _invoices = inject(InvoicesRepository);
	private _receipts = inject(ReceiptsRepository);



	private _nextDocumentNumber$() {
		return this.activeTab() === 'invoice' ? this._invoices.nextInvoiceNumber$() : this._receipts.nextReceiptNumber$();
	}


	private _notificationService = inject(NotificationService);
	private _userModals = inject(UserModals);
	private _translationService = inject(TranslationService);
	private _router = inject(Router);
	private _route = inject(ActivatedRoute);

	private _i18nCreateFailed = this._translationService.prep('Could not create that draft.');
	private _i18nIssueFailed = this._translationService.prep('Could not issue that document.');
	private _i18nDeleteFailed = this._translationService.prep('Could not delete that draft.');
	private _i18nDeleted = this._translationService.prep('Draft deleted.');
	private _i18nSaved = this._translationService.prep('Saved on the draft.');
	private _i18nSaveFailed = this._translationService.prep('Could not save on the draft.');
	private _i18nPreviewFailed = this._translationService.prep('Could not render that document.');
	private _i18nPreviewWithoutXml = this._translationService.prep('That preview printed without its Factur-X XML.');
	private _i18nCheckFailed = this._translationService.prep('Could not have that document checked.');
	private _i18nConfirmDeleteTitle = this._translationService.prep('Delete this draft?');
	private _i18nDeleteExplanation = this._translationService.prep('It has not been issued, so nothing has been allocated and no number will be skipped.');
	private _i18nIssueExplanation = this._translationService.prep(
		'This takes the next number for real, renders the document and shares it with the customer. It can never be edited or deleted afterwards.'
	);
	private _i18nRedrawn = this._translationService.prep('Nothing was allocated. The draft now holds the next free number — press Issue again.');
	private _i18nDelete = this._translationService.prep('Delete it');
	private _i18nConfirmIssueTitle = this._translationService.prep('Issue this document?');
	private _i18nIssue = this._translationService.prep('Issue it');

	private _invoiceTable = viewChild(InvoiceTableComponent);
	private _receiptTable = viewChild(ReceiptTableComponent);

	activeTab = model<DocumentKind>(DEFAULT_TAB);
	tabs: { value: DocumentKind; label: string }[] = [
		{ value: 'invoice', label: 'Invoices' },
		{ value: 'receipt', label: 'Receipts' },
	];

	/** The number a new draft would take. A prediction: nothing is allocated until it is issued. */
	nextNumber = signal<string | null>(null);

	/** The document the server proposes for a new draft: us as the seller, today's dates, the
	 * payment mentions this app is configured with. The editor takes it from there. */
	/** Who the document is for. Chosen through the user picker rather than typed: nobody has a
	 * customer's uuid to hand, and the one place it appears is a support ticket. */
	customer = signal<{ id: string; email?: string | null } | null>(null);
	/** The freshest copy of whichever document was last acted on. */
	documentInFocus = signal<Invoice | Receipt | null>(null);
	proposedDocument = signal<Invoice | Receipt | null>(null);

	constructor() {
		effect(() => {
			const activeTab = this.activeTab();
			if (activeTab === undefined) {
				this.activeTab.set(DEFAULT_TAB);
				return;
			}
			this._router.navigate([], { relativeTo: this._route, queryParams: { activeTab }, queryParamsHandling: 'merge', replaceUrl: true, preserveFragment: true });
			this.refreshNextNumber();
			this.refreshDraftDefaults();
		});
	}

	refreshNextNumber() {
		this._nextDocumentNumber$().subscribe((response) => this.nextNumber.set(response.result?.number ?? null));
	}


	/** Asks the server for a new document of this kind. The id is generated here, as it is for any
	 * other resource, so the editor holds a whole document from the first keystroke. */
	refreshDraftDefaults() {
		const customerId = this.customer()?.id ?? null;
		// Widened where the branch is taken: a union of observables has no callable `.subscribe`.
		const asked: Observable<RequestResponse<InvoiceConfig | ReceiptConfig>> =
			this.activeTab() === 'invoice' ? this._invoices.newInvoiceDraft$(customerId) : this._receipts.newReceiptDraft$(customerId);
		asked.subscribe((response) => {
			const config = response.result;
			if (!config) return;
			this.proposedDocument.set({ id: uuidv4(), number: '', status: 'draft', config } as Invoice | Receipt);
		});
	}

	/** A document that did not exist now does: the list has never heard of it, and the number it
	 * predicted is spent for the next one. */
	onDocumentCreated(document: Invoice | Receipt) {
		this.documentInFocus.set(document);
		this._refreshTable(document.id);
		this.refreshNextNumber();
		this.refreshDraftDefaults();
	}

	setActiveTab(tab: DocumentKind) {
		this.activeTab.set(tab);
		this.documentInFocus.set(null);
	}

	/**
	 * The opened row, in the shape the panel was written for.
	 *
	 * A row carries the stored resource and so does the server's answer, now that the admin
	 * endpoints return the document itself. When the two describe the same one the answer wins, so
	 * a save or an issue shows through without waiting for the store to catch up.
	 */
	/** A deletion moves every later row up a place, so the paginator has to be told. */
	onDocumentDeleted() {
		this.documentInFocus.set(null);
		this._refreshTable();
	}

	/** The number was really taken, so the prediction this page shows is stale. */
	onDocumentIssued(document: Invoice | Receipt) {
		this.documentInFocus.set(document);
		this.refreshNextNumber();
	}

	documentInHand(document: Invoice | Receipt): Invoice | Receipt {
		const inFocus = this.documentInFocus();
		return inFocus?.id === document.id ? inFocus : document;
	}

	/**
	 * The admin endpoints write straight past the store, so the table has to be told to look
	 * again -- and a document just created has no row to open until it does.
	 */
	/**
	 * After a create or a delete, and only because of the paginator: the store is already right,
	 * but pages are not. A new id belongs to no page yet, and a deleted one moves every later row
	 * up a place -- both change what this page holds and how many there are.
	 *
	 * A change to an existing document needs none of this: the row reads the store, and the table
	 * orders by `time_created`, which issuing does not touch.
	 */
	private _refreshTable(openId?: string) {
		const table = this.activeTab() === 'invoice' ? this._invoiceTable() : this._receiptTable();
		if (!table) return;
		table.paginator.refresh();
		if (openId) table.openedItems.selectOnly({ id: openId } as never);
	}



	chooseCustomer() {
		// `bypassAcls` because an admin is picking from every account, not from the ones they
		// happen to hold rights on -- the same call the flag attribution screen makes.
		this._userModals
			.openUsersSelectionDialog({ selectionConstraints: { single: true, maxUsers: 1, minUsers: 1 }, bypassAcls: true })
			.closed.subscribe((result) => {
				const user = result?.users?.[0];
				if (!user) return;
				this.customer.set({ id: user.id, email: user.email });
				// A different customer is a different document: the buyer comes back filled from the
				// billing details they saved themselves, rather than from an admin retyping them.
				this.refreshDraftDefaults();
			});
	}






}
